import { useState } from "react"
import { useSuspenseQuery } from "@tanstack/react-query"
import { Link, createFileRoute } from "@tanstack/react-router"
import { ArrowLeft, ChevronRight, Plus, Users } from "lucide-react"
import { Button } from "@/components/ui/button"
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table"
import { Empty, Header, Hint, SearchBox, SelectField } from "@/components/audience/primitives"
import { formatCount } from "@/lib/download"
import { relativeTime } from "@/lib/format"
import { segmentsQuery } from "@/lib/queries"
import { useCan, useWorkspaceContext } from "@/lib/session"

export const Route = createFileRoute("/w/$workspace/contacts/segments/")({
  loader: ({ context, params }) => context.queryClient.ensureQueryData(segmentsQuery(params.workspace)),
  component: Segments,
})

/** Screen 34. */
function Segments() {
  const { workspace } = useWorkspaceContext()
  const can = useCan()
  const segments = useSuspenseQuery(segmentsQuery(workspace.slug)).data
  const [search, setSearch] = useState("")
  const [usage, setUsage] = useState("all")
  const params = { workspace: workspace.slug }
  const rows = segments.filter((s) => `${s.name} ${s.summary}`.toLowerCase().includes(search.toLowerCase()) && (usage === "all" || (usage === "used" ? s.usedIn.length > 0 : s.usedIn.length === 0)))
  return (
    <div className="page-wrap au-root">
      <Header title="Segments" description="Saved audiences that stay up to date as your contacts change." back={<Link to="/w/$workspace/contacts" params={params} search={{}}><ArrowLeft /> Contacts</Link>}>
        {can("edit_automations") && (
          <Button asChild>
            <Link to="/w/$workspace/contacts/segments/new" params={params}>
              <Plus /> Create segment
            </Link>
          </Button>
        )}
      </Header>
      <div className="au-toolbar">
        <SearchBox value={search} onChange={setSearch} placeholder="Search segments…" />
        <div className="ml-auto w-36">
          <SelectField label="Segment usage" value={usage} onChange={setUsage} options={[{ value: "all", label: "All usage" }, { value: "used", label: "In use" }, { value: "unused", label: "Not used yet" }]} />
        </div>
      </div>
      <div className="au-table">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Segment</TableHead>
              <TableHead>Conditions</TableHead>
              <TableHead className="text-right">People</TableHead>
              <TableHead>Used in</TableHead>
              <TableHead>Updated</TableHead>
              <TableHead />
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.map((segment) => (
              <TableRow key={segment.id}>
                <TableCell>
                  <Link className="au-person text-left" to="/w/$workspace/contacts/segments/$segmentId" params={{ ...params, segmentId: segment.id }}>
                    <span className="au-square-icon">
                      <Users />
                    </span>
                    <span className="font-medium">{segment.name}</span>
                  </Link>
                </TableCell>
                <TableCell className="text-muted-foreground">{segment.summary}</TableCell>
                <TableCell className="text-right font-medium tabular-nums">{segment.matchCount === null ? "—" : formatCount(segment.matchCount)}</TableCell>
                <TableCell>{segment.usedIn.length ? segment.usedIn.join(", ") : "Not used yet"}</TableCell>
                <TableCell className="text-xs text-muted-foreground">{relativeTime(segment.updatedAt)}</TableCell>
                <TableCell>
                  <ChevronRight className="size-3.5 text-muted-foreground" />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        {!rows.length && <Empty noun="segments" hint={segments.length === 0 ? "Create a segment to describe an audience with conditions." : "Try another search or reset your filters."} />}
      </div>
      <div className="mt-6">
        <Hint>Segments describe people. Each campaign checks channel identity, marketing permission and the messaging window separately.</Hint>
      </div>
    </div>
  )
}
